import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  color,
  typography,
} from 'deepstash-ui';
import { CollectionApiResponse } from 'api/api.types';
import NextImage from 'src/components/image/NextImage';
import BookImageCollection from './BookImageCollection';

const SOURCE_SIZE = { WIDTH_CONTAINER: 200, WIDTH: 180, HEIGHT: 250 };
const SOURCE_SIZE_MOBILE = { WIDTH_CONTAINER: 160, WIDTH: 140, HEIGHT: 200 };

const AUTHOR_SIZE = { WIDTH_CONTAINER: 200, WIDTH: 200, HEIGHT: 200 };
const AUTHOR_SIZE_MOBILE = { WIDTH_CONTAINER: 160, WIDTH: 160, HEIGHT: 160 };

const DEFAULT_AUTHOR_IMAGE =
  'https://static.deepstash.com/images/author-placeholder.png';

interface CollectionOverviewSectionProps {
  collection: CollectionApiResponse;
}

const CollectionOverviewSection: React.FC<CollectionOverviewSectionProps> = ({
  collection,
}) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();

  const renderSourcesView = () => {
    if (
      collection.seo?.featured_sources !== undefined &&
      collection.seo.featured_sources.length > 0
    ) {
      return (
        <Flex flexDirection="column" width={'100%'} alignItems="center">
          <Text
            {...(isMobileView
              ? typography.primaryHeadingFonts.small.bold
              : typography.primaryHeadingFonts.large.bold)}
            color={color[colorMode].text}
            textAlign="center"
            {...(isMobileView ? { px: 16 } : {})}
          >
            {`Ideas from books, podcasts, videos & articles`}
          </Text>
          <Flex
            width={'100%'}
            flexDirection="row"
            gridColumnGap={16}
            justifyContent={isMobileView ? 'start' : 'center'}
            mt={-16}
            {...(isMobileView ? { overflow: 'scroll', px: 16, pb: 8 } : {})}
          >
            {collection.seo.featured_sources
              .slice(0, 5)
              .map((source, index) => {
                return (
                  <Flex
                    key={index}
                    w={
                      isMobileView
                        ? SOURCE_SIZE_MOBILE.WIDTH_CONTAINER
                        : SOURCE_SIZE.WIDTH_CONTAINER
                    }
                    flexDirection="column"
                    alignItems="center"
                  >
                    <BookImageCollection
                      size={300}
                      nextImageProps={{
                        imageUrl: source.image,
                        alt: `${source.title}`,
                        priority: true,
                      }}
                    />
                    <Text
                      textAlign="center"
                      {...(isMobileView
                        ? typography.primaryTextFonts.large.regular
                        : typography.primaryHeadingFonts.small.regular)}
                      color={color[colorMode].text}
                      noOfLines={3}
                      w={
                        isMobileView
                          ? SOURCE_SIZE_MOBILE.WIDTH_CONTAINER
                          : SOURCE_SIZE.WIDTH_CONTAINER
                      }
                      mt={-16} // hack
                    >{`${source.title}`}</Text>
                  </Flex>
                );
              })}
          </Flex>
        </Flex>
      );
    } else {
      return null;
    }
  };

  const renderPeopleView = () => {
    if (
      collection.seo?.featured_publishers !== undefined &&
      collection.seo.featured_publishers.length > 0
    ) {
      return (
        <Flex
          flexDirection="column"
          gridRowGap={16}
          width={'100%'}
          alignItems="center"
          mt={isMobileView ? 40 : 88}
        >
          <Text
            {...(isMobileView
              ? typography.primaryHeadingFonts.small.bold
              : typography.primaryHeadingFonts.large.bold)}
            color={color[colorMode].text}
            textAlign="center"
            {...(isMobileView ? { px: 16 } : {})}
          >
            {'Influential people you will learn from'}
          </Text>
          <Flex
            width={'100%'}
            flexDirection="row"
            gridColumnGap={32}
            justifyContent={isMobileView ? 'start' : 'center'}
            mt={16}
            {...(isMobileView ? { overflow: 'scroll', px: 16, pb: 8 } : {})}
          >
            {collection.seo.featured_publishers
              .slice(0, 5)
              .map((publisher, index) => {
                return (
                  <Flex
                    key={index}
                    w={
                      isMobileView
                        ? AUTHOR_SIZE_MOBILE.WIDTH_CONTAINER
                        : AUTHOR_SIZE.WIDTH
                    }
                    flexDirection="column"
                    gridRowGap={16}
                    alignItems="center"
                  >
                    <NextImage
                      wrapperStyle={{
                        width: isMobileView
                          ? AUTHOR_SIZE_MOBILE.WIDTH
                          : AUTHOR_SIZE.WIDTH,
                        height: isMobileView
                          ? AUTHOR_SIZE_MOBILE.HEIGHT
                          : AUTHOR_SIZE.HEIGHT,
                        borderRadius: 200,
                        cursor: 'default',
                      }}
                      imageUrl={publisher.image ?? DEFAULT_AUTHOR_IMAGE}
                      defaultImage={DEFAULT_AUTHOR_IMAGE}
                      alt={`${publisher.name}`}
                      priority={true}
                      objectFit="cover"
                      objectPosition={'top'}
                    />
                    <Text
                      textAlign="center"
                      {...(isMobileView
                        ? typography.primaryTextFonts.large.regular
                        : typography.primaryHeadingFonts.small.regular)}
                      color={color[colorMode].text}
                      noOfLines={3}
                      w={
                        isMobileView
                          ? AUTHOR_SIZE_MOBILE.WIDTH_CONTAINER
                          : AUTHOR_SIZE.WIDTH
                      }
                    >{`${publisher.name}`}</Text>
                  </Flex>
                );
              })}
          </Flex>
        </Flex>
      );
    } else {
      return null;
    }
  };

  return (
    <Flex flexDirection="column" width={'100%'}>
      {renderSourcesView()}
      {renderPeopleView()}
    </Flex>
  );
};

export default CollectionOverviewSection;
